export { onLoadDashboard }

import { getContext } from 'telefunc'
import type { TelefuncContext } from '../../server/auth/telefuncContext.ts'
import { buildLicenseMap } from '../../server/buildLicenseMap.ts'
import { getCustomerById, getLicensedDomainsByCustomer } from '../../server/db/queries.ts'

export type LoadResult = { loggedIn: false } | { loggedIn: true; email: string; license: Record<string, string> }

// One round-trip on page mount; per-form actions live in *Panel.telefunc.ts files.
//
// Unlike the form actions, this load probe does NOT `Abort({ notLoggedIn })` for a
// logged-out caller: at initial load, "not signed in" is an EXPECTED branch, so we return
// it as data and let <Dashboard> render <Login>. (Aborting here would also trip the global
// `useTelefuncAbort` handler, whose `location.reload()` would re-mount → re-probe → abort →
// reload, an infinite loop.) The form actions keep aborting, because for them an expired
// cookie mid-session IS unexpected and the global handler's reload-to-Login is the recovery.
//
// `{ loggedIn: false }` also covers: cookie verifies but the customer row is gone (e.g.
// `pnpm write:purge-tests` wiped a maintainer-flagged customer, or a manual delete in
// disaster recovery) — treat as logged-out rather than rendering an authed shell with blank
// fields.
async function onLoadDashboard(): Promise<LoadResult> {
  // getContext() must run synchronously before any await — see telefunc.com/getContext.
  const { customerId, env } = getContext<TelefuncContext>()
  if (!customerId) return { loggedIn: false }
  const customer = await getCustomerById(customerId, env)
  if (!customer) return { loggedIn: false }
  const domains = await getLicensedDomainsByCustomer(customerId, env)
  return { loggedIn: true, email: customer.email, license: await buildLicenseMap(domains, env) }
}
